import React, { useState } from 'react';
import { 
  ShieldCheck, Lock, CreditCard, Building2, Smartphone, 
  CheckCircle2, ArrowRight, X, Loader2, Sparkles, Copy, Check,
  Wifi
} from 'lucide-react';
import { INSTITUTION_INFO } from '../../data/portalData';

export const PaystackModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  amount = 30000, 
  purpose = "Non-Refundable Registration Fee (2025/2026 Session)",
  applicantEmail = "executive.candidate@aksu.edu.ng",
  applicantName = "Candidate Name"
}) => {
  const [channel, setChannel] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(false);

  // Card Form State
  const [cardNumber, setCardNumber] = useState('4084 0820 1192 3844');
  const [cardExpiry, setCardExpiry] = useState('09/28');
  const [cardCvv, setCardCvv] = useState('419');

  if (!isOpen) return null;

  const handleSimulatePayment = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setSuccess(true);
      const generatedRef = "PSTK_" + Math.random().toString(36).substring(2, 11).toUpperCase();
      setTimeout(() => {
        onSuccess({
          reference: generatedRef,
          amount: amount,
          channel: channel,
          paidAt: new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
        });
      }, 1200);
    }, 1800);
  };

  const handleCopyAccount = () => {
    navigator.clipboard?.writeText("9928174401");
    setCopiedAccount(true);
    setTimeout(() => setCopiedAccount(false), 2000);
  };

  // Determine card type based on number
  const isVisa = cardNumber.startsWith('4');
  const isMastercard = cardNumber.startsWith('5');
  const isVerve = cardNumber.startsWith('506') || cardNumber.startsWith('6500');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl border-2 border-slate-200 relative animate-in zoom-in-95 duration-200">
        
        {/* Paystack Official Header */}
        <div className="bg-[#001428] text-white p-5 flex items-start justify-between border-b border-cyan-900/40 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-32 h-32 bg-[#00C3F7]/10 rounded-full blur-xl pointer-events-none" />

          <div className="flex items-center space-x-3 relative z-10">
            <div className="w-10 h-10 rounded-xl bg-[#00C3F7] text-slate-950 flex items-center justify-center font-black text-xl shadow-md">
              P
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-bold text-sm tracking-wide text-white">Paystack Checkout</span>
                <span className="text-[10px] bg-[#002447] text-[#00C3F7] font-mono px-2 py-0.5 rounded-full border border-[#00C3F7]/40 font-bold">
                  LIVE SECURE
                </span>
              </div>
              <div className="text-xs text-slate-300 mt-0.5 font-sc">
                {INSTITUTION_INFO.shortName}
              </div>
            </div>
          </div>

          <button 
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors relative z-10 cursor-pointer"
            title="Cancel transaction"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Invoice Summary Strip */}
        <div className="bg-slate-50 px-5 py-3.5 border-b border-slate-200 flex items-center justify-between text-xs">
          <div>
            <span className="text-slate-500 block text-[11px]">Transaction For:</span>
            <span className="font-bold text-slate-800 font-sc">{purpose}</span>
          </div>
          <div className="text-right">
            <span className="text-slate-500 block text-[11px]">Amount Payable:</span>
            <span className="text-lg font-black text-[#0B2644] font-mono">
              ₦{amount.toLocaleString()}.00
            </span>
          </div>
        </div>

        {success ? (
          /* Payment Completed State */
          <div className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto animate-bounce shadow-inner">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h4 className="text-2xl font-black font-sc text-slate-900">
              Payment Successful!
            </h4>
            <p className="text-xs text-slate-600 leading-relaxed max-w-xs mx-auto">
              Your payment has been cleared by the University Bursary via Paystack. Generating your official payment receipt and logging your application docket for admissions screening...
            </p>
            <div className="text-xs font-mono bg-slate-100 p-2.5 rounded-xl text-slate-700">
              Transfer cleared · Reference verified
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Channel Selection Tabs */}
            <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100 rounded-2xl">
              <button
                type="button"
                onClick={() => setChannel('card')}
                className={`py-2 text-xs font-bold rounded-xl flex items-center justify-center space-x-1.5 transition-all cursor-pointer font-sc ${
                  channel === 'card'
                    ? 'bg-white text-slate-950 shadow-sm border border-slate-200'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <CreditCard className="w-3.5 h-3.5" />
                <span>Card</span>
              </button>

              <button
                type="button"
                onClick={() => setChannel('transfer')}
                className={`py-2 text-xs font-bold rounded-xl flex items-center justify-center space-x-1.5 transition-all cursor-pointer font-sc ${
                  channel === 'transfer'
                    ? 'bg-white text-slate-950 shadow-sm border border-slate-200'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                <span>Transfer</span>
              </button>

              <button
                type="button"
                onClick={() => setChannel('ussd')}
                className={`py-2 text-xs font-bold rounded-xl flex items-center justify-center space-x-1.5 transition-all cursor-pointer font-sc ${
                  channel === 'ussd'
                    ? 'bg-white text-slate-950 shadow-sm border border-slate-200'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span>USSD</span>
              </button>
            </div>

            {/* CHANNEL 1: CARD CHECKOUT */}
            {channel === 'card' && (
              <div className="space-y-4">
                
                {/* Visual Digital Credit Card Preview with Fine Executive Colors */}
                <div className="bg-gradient-to-tr from-[#001428] via-[#05264C] to-[#0A3D68] text-white p-4 sm:p-5 rounded-2xl border-2 border-[#00C3F7]/30 shadow-lg relative overflow-hidden">
                  <div className="absolute right-0 top-0 w-36 h-36 bg-[#00C3F7]/15 rounded-full blur-2xl pointer-events-none" />

                  {/* Card Top Row: Chip & Contactless */}
                  <div className="flex items-center justify-between mb-4 relative z-10">
                    <div className="flex items-center space-x-2">
                      {/* EMV Gold Chip Representation */}
                      <div className="w-9 h-7 rounded-md bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 p-0.5 shadow-xs border border-amber-300">
                        <div className="w-full h-full border border-amber-800/40 rounded-sm grid grid-cols-2 gap-0.5 opacity-80" />
                      </div>
                      <Wifi className="w-4 h-4 text-slate-300 rotate-90" />
                    </div>

                    {/* Network Badges */}
                    <div className="flex items-center space-x-1.5 bg-black/30 px-2 py-0.5 rounded-lg border border-white/10">
                      <span className="text-[10px] font-black italic tracking-wide text-white">VISA</span>
                      <span className="text-white/40">|</span>
                      <div className="flex -space-x-1">
                        <div className="w-3 h-3 rounded-full bg-red-500 opacity-90" />
                        <div className="w-3 h-3 rounded-full bg-amber-400 opacity-90" />
                      </div>
                      <span className="text-white/40">|</span>
                      <span className="text-[9px] font-bold italic text-cyan-300">verve</span>
                    </div>
                  </div>

                  {/* Card Number Display */}
                  <div className="font-mono text-sm sm:text-base tracking-widest text-white font-bold my-2 select-none relative z-10">
                    {cardNumber || "••••  ••••  ••••  ••••"}
                  </div>

                  {/* Card Bottom Row: Holder & Expiry */}
                  <div className="flex items-end justify-between pt-2 border-t border-white/15 text-[10px] uppercase font-mono tracking-wider relative z-10">
                    <div>
                      <span className="text-slate-400 text-[9px] block">Cardholder</span>
                      <span className="text-white font-bold truncate max-w-[180px] block font-sans">
                        {applicantName || "EXECUTIVE CANDIDATE"}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-slate-400 text-[9px] block">Expires</span>
                      <span className="text-white font-bold">{cardExpiry || "MM/YY"}</span>
                    </div>
                  </div>
                </div>

                {/* Clean Form Inputs with Proper Spacing & Fine Borders */}
                <div className="space-y-3">
                  {/* Card Number Input */}
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                      Card Number
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        placeholder="4084 0820 1192 3844"
                        className="w-full text-xs sm:text-sm font-mono p-2.5 pl-3 pr-10 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-[#00C3F7] focus:bg-white transition-colors"
                      />
                      <CreditCard className="w-4 h-4 text-slate-400 absolute right-3 top-3 pointer-events-none" />
                    </div>
                  </div>

                  {/* Expiry & CVV Inputs */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                        Expiry Date
                      </label>
                      <input
                        type="text"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(e.target.value)}
                        placeholder="MM/YY"
                        maxLength={5}
                        className="w-full text-xs sm:text-sm font-mono p-2.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-[#00C3F7] focus:bg-white transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                        CVV (Security Code)
                      </label>
                      <input
                        type="password"
                        maxLength={4}
                        value={cardCvv}
                        onChange={(e) => setCardCvv(e.target.value)}
                        placeholder="123"
                        className="w-full text-xs sm:text-sm font-mono p-2.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-[#00C3F7] focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  {/* Encryption Status Notice */}
                  <div className="text-[11px] text-slate-500 flex items-center pt-0.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 mr-1.5 shrink-0" />
                    <span>256-bit bank-grade SSL encrypted · PCI-DSS Level 1 compliant</span>
                  </div>
                </div>

              </div>
            )}

            {/* CHANNEL 2: BANK TRANSFER */}
            {channel === 'transfer' && (
              <div className="p-4 rounded-2xl bg-slate-50 border-2 border-slate-200 space-y-3 text-xs">
                <div className="text-slate-600">
                  Transfer exactly <strong className="text-slate-900 font-mono">₦{amount.toLocaleString()}</strong> to this dynamic Paystack reserve account:
                </div>

                <div className="p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between shadow-2xs">
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase font-semibold block font-sc">Bank Name</span>
                    <span className="font-bold text-slate-900">Titan Trust Bank / Paystack</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold block font-sc">Account Number</span>
                    <div className="flex items-center space-x-1.5">
                      <span className="font-mono font-bold text-sm text-slate-900">9928174401</span>
                      <button 
                        onClick={handleCopyAccount}
                        className="text-slate-500 hover:text-slate-900 cursor-pointer"
                        title="Copy account number"
                      >
                        {copiedAccount ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="text-[11px] text-amber-800 bg-amber-50 p-2.5 rounded-xl border border-amber-200">
                  Account expires in 29:59 minutes. Automated receipt generated upon bank clearance.
                </div>
              </div>
            )}

            {/* CHANNEL 3: USSD */}
            {channel === 'ussd' && (
              <div className="p-4 rounded-2xl bg-slate-50 border-2 border-slate-200 space-y-3 text-xs">
                <div className="text-slate-600">
                  Select your bank to dial the instant Paystack USSD string:
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200 font-mono text-center font-bold text-slate-800 shadow-2xs">
                    GTBank: *737*000*884#
                  </div>
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200 font-mono text-center font-bold text-slate-800 shadow-2xs">
                    Zenith: *966*000*884#
                  </div>
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200 font-mono text-center font-bold text-slate-800 shadow-2xs">
                    UBA: *919*000*884#
                  </div>
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200 font-mono text-center font-bold text-slate-800 shadow-2xs">
                    Access: *901*000*884#
                  </div>
                </div>
              </div>
            )}

            {/* Pay Button in Paystack Electric Cyan */}
            <button
              type="button"
              disabled={processing}
              onClick={handleSimulatePayment}
              className="w-full py-3.5 bg-[#00C3F7] hover:bg-[#00ace0] text-slate-950 font-black rounded-xl text-sm transition-all shadow-md flex items-center justify-center space-x-2 cursor-pointer disabled:opacity-50 font-sc"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-slate-950" />
                  <span>Authorizing via Paystack...</span>
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  <span>Pay ₦{amount.toLocaleString()}.00 Now</span>
                </>
              )}
            </button>

            {/* Bottom Security Guarantee */}
            <div className="flex items-center justify-center space-x-2 text-[11px] text-slate-400 pt-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              <span>PCI-DSS Level 1 Certified Payment Gateway</span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
